import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import {
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledReset,
} from '../styles';

const actionBase = css`
    &:disabled,
    &[data-link-disabled],
    &[data-button-static] {
        ${styledInteraction.interactionDisable};
    }
`;

export const transitionProps = ['color', 'background-color', 'border-color', 'outline-color', 'opacity'];

export const ExternalLink = styled.a.attrs({ target: '_blank', rel: 'noopener noreferrer' })`
    ${styledReset.link};
    ${actionBase};
`;

export const InternalLink = styled(Link)`
    ${styledReset.link};
    ${actionBase};
`;

export const PrimitiveButton = styled.button`
    ${styledReset.button};
    ${actionBase};
`;

///
/// Recipes

// TODO: Include a common set of sizes.
// Example: height = 24 / 32 / 40 / 48 / 64 / 100

const link = css`
    // TODO: Remove 'important!' once we no longer have
    // the old global reset styles interferring.
    text-decoration: underline 1px transparent !important;
    transition-property: opacity, color, text-decoration-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};
`;

const essential = css`
    outline: 2px solid transparent !important;
    outline-offset: 2px;
    transition-property: ${transitionProps.join(',')};
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &:focus-visible {
        outline-color: ${designColor.brandPurple} !important;
    }
`;

const basic = css`
    ${essential};
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
`;

const blur = css`
    ${basic};
    background-color: rgb(255, 255, 255, 0.1);
    backdrop-filter: blur(4px);

    &:hover:not(:active) {
        background-color: rgb(255, 255, 255, 0.16);
    }
`;

const blurDark = css`
    ${basic};
    background-color: rgb(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);

    &:hover:not(:active) {
        background-color: rgb(0, 0, 0, 0.6);
    }
`;

const primary = css`
    ${basic};
    background-color: ${designColor.brandPurple};

    &:hover:not(:active) {
        background-color: ${designColor.brandPurpleDark};
    }
`;

const secondary = css`
    ${basic};
    background-color: rgb(255, 255, 255, 0.4);

    &:hover:not(:active) {
        background-color: rgb(255, 255, 255, 0.5);
    }
`;

///
/// Extensions

const gradientBase = css`
    z-index: 1;
    position: relative;

    &::before {
        ${styledInteraction.interactionDisable};
        content: '';
        z-index: 2;
        position: absolute;
        inset: 0;
        display: block;
        border-radius: 999px;
        background-position: center;
        background-size: 100%;
        transition-property: opacity, background-size;
        transition-duration: ${duration.normal}ms;
        transition-timing-function: ${easing.cubic};
    }

    &:hover:not(:active)::before {
        background-size: 140%;
    }
`;

const gradientPrimary = css`
    ${gradientBase};

    &::before {
        // prettier-ignore
        background-image: linear-gradient(
            120deg,
            ${designColor.brandPurpleLighter},
            ${designColor.brandPurple} 80%
        );
    }
`;

const gradientRadical = css`
    ${gradientBase};

    &::before {
        background-image: linear-gradient(120deg, #aa039f, #f00050, #f67916);
    }
`;

const afterGlowBase = css`
    position: relative;

    &::after {
        ${styledInteraction.interactionDisable};
        content: '';
        z-index: 2;
        position: absolute;
        inset: 0;
        display: block;
        color: rgb(255, 255, 255, 0.6);
        border-radius: 999px;
        box-shadow:
            inset 0 0 0.6em currentColor,
            0 0 1.6em currentColor;
        transition: opacity ${duration.normal}ms ${easing.cubic};
    }
`;

const afterGlowPrimary = css`
    ${afterGlowBase};

    &::after {
        color: rgb(82, 60, 240, 0.6);
    }
`;

const afterGlowRadical = css`
    ${afterGlowBase};

    &::after {
        color: rgb(240, 0, 80, 0.6);
    }
`;

export const recipesCommonAction = {
    link,
    essential,
    basic,
    blur,
    blurDark,
    primary,
    secondary,
};

export const extCommonAction = {
    transitionProps,
    gradientBase,
    gradientPrimary,
    gradientRadical,
    afterGlowBase,
    afterGlowPrimary,
    afterGlowRadical,
};
